import React, { useState, useEffect, useCallback } from 'react';
import API_ENDPOINTS from '../config/api';
import ContextPackPanel from './ContextPackPanel';
import AutomationInventory from './AutomationInventory';
import GraphExplorer from './GraphExplorer';

const DocumentationBrowser = () => {
  const [scanStatus, setScanStatus] = useState({ status: 'none' });
  const [objects, setObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [objectDetail, setObjectDetail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'standard', 'custom'
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');
  const [editingObjectDesc, setEditingObjectDesc] = useState({ active: false, text: '' });
  const [editingFieldDesc, setEditingFieldDesc] = useState({ fieldName: null, text: '' });
  const [saving, setSaving] = useState(false);
  const [acceptingAll, setAcceptingAll] = useState(false);
  const [selectedForGeneration, setSelectedForGeneration] = useState(new Set());
  const [orgContextNote, setOrgContextNote] = useState('');
  const [objectDescEdits, setObjectDescEdits] = useState({}); // { objectName: "text" }
  const [isGenerating, setIsGenerating] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [reviewingObject, setReviewingObject] = useState(null);
  const [ignoredObjects, setIgnoredObjects] = useState([]);
  const [ignoredCount, setIgnoredCount] = useState(0);

  // Flow tab state
  const [activeTab, setActiveTab] = useState('objects'); // 'objects' | 'flows'
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [flowDetail, setFlowDetail] = useState(null);
  const [flowDetailLoading, setFlowDetailLoading] = useState(false);
  const [flowSearchQuery, setFlowSearchQuery] = useState('');
  const [flowFilterType, setFlowFilterType] = useState('all');
  const [selectedFlowsForGeneration, setSelectedFlowsForGeneration] = useState(new Set());
  const [flowScanStatus, setFlowScanStatus] = useState('none');
  const [isFlowScanning, setIsFlowScanning] = useState(false);
  const [isFlowGenerating, setIsFlowGenerating] = useState(false);
  const [flowGenerateProgress, setFlowGenerateProgress] = useState(0);
  const [flowScanProgress, setFlowScanProgress] = useState({ total: 0, scanned: 0, progress: 0 });
  const [flowConnections, setFlowConnections] = useState([]);
  const [editingFlowDesc, setEditingFlowDesc] = useState({ active: false, text: '' });
  const [graphTarget, setGraphTarget] = useState(null);
  const [flowsForObject, setFlowsForObject] = useState([]);
  const [expandedFormulas, setExpandedFormulas] = useState(new Set());
  const [isExporting, setIsExporting] = useState(false);

  const toggleFormula = (fieldName) => {
    setExpandedFormulas(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) next.delete(fieldName);
      else next.add(fieldName);
      return next;
    });
  };

  const exportDataDictionary = async () => {
    setIsExporting(true);
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_EXPORT_DATA_DICTIONARY, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Export failed');
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'DataDictionary.csv';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Data dictionary export failed:', err);
      setError(err.message);
    } finally {
      setIsExporting(false);
    }
  };

  const getAuthHeaders = () => {
    const session = JSON.parse(localStorage.getItem('forcesight_session') || '{}');
    const sf = session.salesforce || {};
    return {
      'Content-Type': 'application/json',
      'X-SF-Instance-URL': sf.instanceUrl || '',
      'X-SF-Access-Token': sf.accessToken || '',
    };
  };

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_STATUS, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setScanStatus(data);
      }
      return data;
    } catch (err) {
      console.error('Failed to fetch status:', err);
      return null;
    }
  }, []);

  const fetchObjects = useCallback(async (restoreSelection = false) => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_OBJECTS, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setObjects(data.objects);
        if (restoreSelection) {
          const selected = data.objects.filter(o => o.selectedForGeneration);
          if (selected.length > 0) {
            setSelectedForGeneration(new Set(selected.map(o => o.name)));
            const edits = {};
            selected.forEach(o => { edits[o.name] = o.aiDescription || ''; });
            setObjectDescEdits(edits);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch objects:', err);
    }
  }, []);

  const fetchIgnoredCount = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_IGNORED_OBJECTS, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setIgnoredCount(data.count);
      }
    } catch (err) {
      console.error('Failed to fetch ignored count:', err);
    }
  }, []);

  // --- Flow Functions ---

  const fetchFlows = useCallback(async (restoreSelection = false) => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_FLOWS, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setFlows(data.flows);
        if (restoreSelection) {
          const selected = data.flows.filter(f => f.selectedForGeneration);
          if (selected.length > 0) {
            setSelectedFlowsForGeneration(new Set(selected.map(f => f.apiName)));
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch flows:', err);
    }
  }, []);

  const fetchFlowScanStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.DOCUMENTATION_FLOWS_SCAN}/status`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setFlowScanStatus(data.status);
        setFlowScanProgress({ total: data.totalFlows, scanned: data.scannedFlows, progress: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch flow scan status:', err);
    }
  }, []);

  const fetchFlowDetail = async (apiName) => {
    setFlowDetailLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.DOCUMENTATION_FLOWS}/${apiName}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setFlowDetail(data.flow);
      }
    } catch (err) {
      console.error('Failed to fetch flow detail:', err);
    } finally {
      setFlowDetailLoading(false);
    }
  };

  const fetchFlowConnections = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_FLOWS_CONNECTIONS, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setFlowConnections(data.connections);
      }
    } catch (err) {
      console.error('Failed to fetch flow connections:', err);
    }
  };

  const fetchFlowsForObject = async (objectName) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.DOCUMENTATION_FLOWS}/impact/${objectName}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setFlowsForObject(data.flows);
      }
    } catch (err) {
      console.error('Failed to fetch flows for object:', err);
    }
  };

  const startFlowScan = async () => {
    setIsFlowScanning(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_FLOWS_SCAN, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to start flow scan');

      setFlowScanProgress({ total: data.totalFlows, scanned: 0, progress: 0 });
      setFlowScanStatus('scanning');

      // Continue scanning in batches
      let completed = false;
      while (!completed) {
        const contResp = await fetch(API_ENDPOINTS.DOCUMENTATION_FLOWS_SCAN_CONTINUE, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        const contData = await contResp.json();
        if (!contData.success) throw new Error(contData.message || 'Flow scan batch failed');

        setFlowScanProgress(prev => ({
          ...prev,
          scanned: prev.scanned + contData.scanned,
          progress: contData.progress,
        }));

        completed = contData.completed;
      }

      setFlowScanStatus('complete');
      await fetchFlows(true);
      await fetchFlowConnections();
    } catch (err) {
      setError(err.message);
      setFlowScanStatus('error');
    } finally {
      setIsFlowScanning(false);
    }
  };

  const toggleSelectFlow = (apiName) => {
    setSelectedFlowsForGeneration(prev => {
      const next = new Set(prev);
      if (next.has(apiName)) {
        next.delete(apiName);
      } else {
        next.add(apiName);
      }
      return next;
    });
  };

  const persistFlowSelection = async (names) => {
    try {
      await fetch(API_ENDPOINTS.DOCUMENTATION_FLOWS_SELECT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ flowApiNames: names }),
      });
    } catch (err) {
      console.error('Failed to persist flow selection:', err);
    }
  };

  const generateForSelectedFlows = async () => {
    setError(null);
    const names = Array.from(selectedFlowsForGeneration);
    if (names.length === 0) return;

    await persistFlowSelection(names);
    setIsFlowGenerating(true);
    setFlowGenerateProgress(0);

    let completed = false;
    let firstCall = true;

    while (!completed) {
      try {
        const response = await fetch(API_ENDPOINTS.DOCUMENTATION_FLOWS_GENERATE, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(firstCall ? { guided: true } : {}),
        });
        firstCall = false;
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Server error (${response.status}): ${text.slice(0, 200)}`);
        }
        const data = await response.json();
        if (!data.success) throw new Error(data.message || 'Flow AI generation failed');

        setFlowGenerateProgress(data.progress);
        completed = data.completed;
      } catch (err) {
        setError(err.message);
        setIsFlowGenerating(false);
        return;
      }
    }

    await fetchFlows();
    setIsFlowGenerating(false);
  };

  const acceptFlowSuggestion = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_FLOWS}/${selectedFlow}/suggestion/accept`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to accept');
      setFlowDetail(prev => ({
        ...prev,
        aiDescription: prev.aiSuggestedDescription,
        aiSuggestedDescription: null,
        aiStatus: 'complete',
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const dismissFlowSuggestion = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_FLOWS}/${selectedFlow}/suggestion/dismiss`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to dismiss');
      setFlowDetail(prev => ({
        ...prev,
        aiSuggestedDescription: null,
        aiStatus: 'dismissed',
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveFlowDescription = async (description) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_FLOWS}/${selectedFlow}/description`,
        { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ description }) }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to save');
      setFlowDetail(prev => ({ ...prev, aiDescription: description }));
      setEditingFlowDesc({ active: false, text: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const fetchObjectDetail = async (objectName) => {
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${objectName}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setObjectDetail(data.object);
      }
    } catch (err) {
      console.error('Failed to fetch object detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const saveObjectDescription = async (description) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${selectedObject}/description`,
        { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ description }) }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to save');
      setObjectDetail(prev => ({ ...prev, aiDescription: description }));
      setEditingObjectDesc({ active: false, text: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveFieldDescription = async (fieldName, description) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${selectedObject}/fields/${fieldName}/description`,
        { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ description }) }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to save');
      setObjectDetail(prev => ({
        ...prev,
        fields: prev.fields.map(f =>
          f.name === fieldName ? { ...f, aiDescription: description } : f
        ),
      }));
      setEditingFieldDesc({ fieldName: null, text: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const acceptObjectSuggestion = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${selectedObject}/suggestion/accept`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to accept');
      setObjectDetail(prev => ({
        ...prev,
        aiDescription: prev.aiSuggestedDescription,
        aiSuggestedDescription: null,
        aiStatus: 'complete',
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const dismissObjectSuggestion = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${selectedObject}/suggestion/dismiss`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to dismiss');
      setObjectDetail(prev => ({
        ...prev,
        aiSuggestedDescription: null,
        aiStatus: 'dismissed',
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const acceptFieldSuggestion = async (fieldName) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${selectedObject}/fields/${fieldName}/suggestion/accept`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to accept');
      setObjectDetail(prev => ({
        ...prev,
        fields: prev.fields.map(f =>
          f.name === fieldName
            ? { ...f, aiDescription: f.aiSuggestedDescription, aiSuggestedDescription: null, aiStatus: 'complete' }
            : f
        ),
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const dismissFieldSuggestion = async (fieldName) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${selectedObject}/fields/${fieldName}/suggestion/dismiss`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to dismiss');
      setObjectDetail(prev => ({
        ...prev,
        fields: prev.fields.map(f =>
          f.name === fieldName
            ? { ...f, aiSuggestedDescription: null, aiStatus: 'dismissed' }
            : f
        ),
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const acceptAllSuggestions = async () => {
    setAcceptingAll(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${selectedObject}/suggestion/accept-all`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to accept all');
      // Refresh the detail view
      await fetchObjectDetail(selectedObject);
    } catch (err) {
      setError(err.message);
    } finally {
      setAcceptingAll(false);
    }
  };

  const fetchOrgContext = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_CONTEXT, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setOrgContextNote(data.userNote || '');
      }
    } catch (err) {
      console.error('Failed to fetch org context:', err);
    }
  }, []);

  const saveOrgContext = async () => {
    try {
      await fetch(API_ENDPOINTS.DOCUMENTATION_CONTEXT, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ note: orgContextNote }),
      });
    } catch (err) {
      console.error('Failed to save org context:', err);
    }
  };

  const persistSelection = async (names) => {
    try {
      await fetch(API_ENDPOINTS.DOCUMENTATION_SELECT_OBJECTS, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ objectNames: names }),
      });
    } catch (err) {
      console.error('Failed to persist selection:', err);
    }
  };

  const saveObjectDescriptionDirect = async (objectName, description) => {
    try {
      await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${objectName}/description`,
        { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ description }) }
      );
    } catch (err) {
      console.error('Failed to save object description:', err);
    }
  };

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchStatus();
      await fetchObjects(true); // restore selection from DB
      await fetchOrgContext();
      await fetchIgnoredCount();
      await fetchFlowScanStatus();
      await fetchFlows(true);
      setLoading(false);
    };
    init();
  }, [fetchStatus, fetchObjects, fetchOrgContext, fetchIgnoredCount, fetchFlowScanStatus, fetchFlows]);

  // Start scan
  const startScan = async () => {
    setIsScanning(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_SCAN, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to start scan');
      }

      setScanStatus({ status: 'scanning', progress: 0, totalObjects: data.totalObjects, scannedObjects: 0 });

      // Continue scanning in batches
      await continueScan();
    } catch (err) {
      setError(err.message);
      setIsScanning(false);
    }
  };

  const continueScan = async () => {
    let completed = false;
    while (!completed) {
      try {
        const response = await fetch(API_ENDPOINTS.DOCUMENTATION_SCAN_CONTINUE, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        const data = await response.json();
        if (!data.success) {
          throw new Error(data.message || 'Scan batch failed');
        }

        setScanStatus(prev => ({
          ...prev,
          progress: data.progress,
          scannedObjects: (prev.scannedObjects || 0) + data.scanned,
        }));

        completed = data.completed;
      } catch (err) {
        setError(err.message);
        setIsScanning(false);
        setScanStatus(prev => ({ ...prev, status: 'error' }));
        return;
      }
    }

    // Scan complete — return to browse (user can select objects to generate)
    setScanStatus(prev => ({ ...prev, status: 'scan_complete', progress: 100 }));
    await fetchObjects();
    setIsScanning(false);
  };

  // Select an object
  const handleSelectObject = (obj) => {
    setSelectedObject(obj.name);
    setFieldSearch('');
    fetchObjectDetail(obj.name);
    fetchFlowsForObject(obj.name);
  };

  // Filter objects
  const filteredObjects = filterType === 'ignored' ? [] : objects.filter(obj => {
    if (filterType === 'standard' && obj.custom) return false;
    if (filterType === 'custom' && !obj.custom) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        obj.name.toLowerCase().includes(q) ||
        obj.label.toLowerCase().includes(q) ||
        (obj.aiDescription && obj.aiDescription.toLowerCase().includes(q))
      );
    }
    return true;
  });

  // Filter flows
  const filteredFlows = flows.filter(flow => {
    if (flowFilterType !== 'all') {
      const typeMap = {
        'record-triggered': f => f.triggerType && (f.triggerType.startsWith('Record') || (f.processType === 'AutoLaunchedFlow' && f.triggerObject)),
        'screen': f => f.processType === 'Flow',
        'scheduled': f => (f.processType === 'CustomEvent') || (f.triggerType === 'Scheduled'),
        'auto': f => f.processType === 'AutoLaunchedFlow' && !f.triggerObject,
      };
      const filterFn = typeMap[flowFilterType];
      if (filterFn && !filterFn(flow)) return false;
    }
    if (flowSearchQuery) {
      const q = flowSearchQuery.toLowerCase();
      return (
        flow.apiName.toLowerCase().includes(q) ||
        (flow.label && flow.label.toLowerCase().includes(q)) ||
        (flow.triggerObject && flow.triggerObject.toLowerCase().includes(q)) ||
        (flow.aiDescription && flow.aiDescription.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const hasFlowSelection = selectedFlowsForGeneration.size > 0;

  // Filter fields within selected object
  const filteredFields = objectDetail?.fields?.filter(f => {
    if (!fieldSearch) return true;
    const q = fieldSearch.toLowerCase();
    return (
      f.name.toLowerCase().includes(q) ||
      f.label.toLowerCase().includes(q) ||
      (f.aiDescription && f.aiDescription.toLowerCase().includes(q))
    );
  }) || [];

  // --- Selection & Generation Handlers ---

  const toggleSelectObject = (name) => {
    setSelectedForGeneration(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
        // Clean up edit state
        setObjectDescEdits(prev => { const n = { ...prev }; delete n[name]; return n; });
        if (reviewingObject === name) setReviewingObject(null);
      } else {
        next.add(name);
        // Pre-load existing description
        const obj = objects.find(o => o.name === name);
        setObjectDescEdits(prev => ({ ...prev, [name]: obj?.aiDescription || '' }));
      }
      return next;
    });
  };

  const handleDescriptionEdit = (objectName, text) => {
    setObjectDescEdits(prev => ({ ...prev, [objectName]: text }));
  };

  const handleDescriptionBlur = (objectName) => {
    if (objectDescEdits[objectName] !== undefined) {
      saveObjectDescriptionDirect(objectName, objectDescEdits[objectName]);
    }
  };

  const ignoreObject = async (name) => {
    try {
      await fetch(`${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${name}/ignore`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      setObjects(prev => prev.filter(o => o.name !== name));
      setSelectedForGeneration(prev => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      setObjectDescEdits(prev => { const n = { ...prev }; delete n[name]; return n; });
      if (reviewingObject === name) setReviewingObject(null);
      setIgnoredCount(prev => prev + 1);
    } catch (err) {
      console.error('Failed to ignore object:', err);
    }
  };

  const fetchIgnoredObjects = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_IGNORED_OBJECTS, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setIgnoredObjects(data.objects);
        setIgnoredCount(data.count);
      }
    } catch (err) {
      console.error('Failed to fetch ignored objects:', err);
    }
  };

  const unignoreObject = async (name) => {
    try {
      await fetch(`${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${name}/unignore`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      setIgnoredObjects(prev => prev.filter(o => o.name !== name));
      setIgnoredCount(prev => Math.max(0, prev - 1));
      await fetchObjects();
    } catch (err) {
      console.error('Failed to unignore object:', err);
    }
  };

  const generateForSelected = async () => {
    setError(null);
    const names = Array.from(selectedForGeneration);
    if (names.length === 0) return;

    // Save all pending object descriptions
    for (const name of names) {
      if (objectDescEdits[name] !== undefined) {
        await saveObjectDescriptionDirect(name, objectDescEdits[name]);
      }
    }

    // Save org context + persist selection
    await saveOrgContext();
    await persistSelection(names);

    // Start generation
    setIsGenerating(true);
    setScanStatus(prev => ({ ...prev, status: 'generating', progress: 0 }));
    let completed = false;
    let firstCall = true;

    while (!completed) {
      try {
        const response = await fetch(API_ENDPOINTS.DOCUMENTATION_GENERATE, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(firstCall ? { guided: true } : {}),
        });
        firstCall = false;
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Server error (${response.status}): ${text.slice(0, 200)}`);
        }
        const data = await response.json();
        if (!data.success) {
          throw new Error(data.message || 'AI generation failed');
        }

        setScanStatus(prev => ({
          ...prev,
          status: data.completed ? 'complete' : 'generating',
          progress: data.progress,
        }));

        completed = data.completed;
      } catch (err) {
        setError(err.message);
        setScanStatus(prev => ({ ...prev, status: 'error' }));
        setIsGenerating(false);
        return;
      }
    }

    await fetchObjects();
    // Keep selection so user can review suggestions for the objects they just generated
    setIsGenerating(false);
  };

  const deployObject = async (objectName) => {
    setDeploying(true);
    setDeployResult(null);
    setError(null);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${objectName}/deploy`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Deploy failed');
      setDeployResult(data.results);
      setObjectDetail(prev => prev ? { ...prev, deployedToSfAt: new Date().toISOString() } : prev);
      await fetchObjects();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeploying(false);
    }
  };

  const deploySelected = async () => {
    const deployableObjects = objects.filter(
      o => selectedForGeneration.has(o.name) && (o.aiDescription || o.fields?.some(f => f.aiDescription))
    );
    if (deployableObjects.length === 0) return;

    setDeploying(true);
    setDeployResult(null);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_DEPLOY, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ objectNames: deployableObjects.map(o => o.name) }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Deploy failed');
      setDeployResult(data.results);
      await fetchObjects();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeploying(false);
    }
  };

  if (loading) {
    return (
      <div className="doc-browser">
        <div className="doc-loading">
          <div className="lightsaber-loader"></div>
          <p>Loading documentation engine...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // No scan state
  if (scanStatus.status === 'none' && objects.length === 0) {
    return (
      <div className="doc-browser">
        <div className="doc-empty-state">
          <div className="doc-empty-icon">📚</div>
          <h2>Documentation Engine</h2>
          <p>
            Catalog every Salesforce object and field in your org with AI-powered descriptions.
            Get instant clarity about your org's metadata.
          </p>
          <button className="doc-scan-btn" onClick={startScan} disabled={isScanning} title="Scan all objects and fields in your Salesforce org">
            {isScanning ? 'Scanning...' : 'Start Documentation Scan'}
          </button>
          {error && <p className="doc-error">{error}</p>}
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Scanning/generating state — only show when user actively started a scan this session
  if (isScanning) {
    const statusLabel = scanStatus.status === 'generating'
      ? 'Generating AI descriptions...'
      : `Scanning objects... ${scanStatus.scannedObjects || 0} of ${scanStatus.totalObjects || '?'}`;

    return (
      <div className="doc-browser">
        <div className="doc-scanning-state">
          <h2>Documentation Engine</h2>
          <p className="doc-scan-status">{statusLabel}</p>
          <div className="doc-progress-bar">
            <div
              className="doc-progress-fill"
              style={{ width: `${scanStatus.progress || 0}%` }}
            />
          </div>
          <p className="doc-progress-text">{scanStatus.progress || 0}% complete</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Generating state overlay
  if (isGenerating) {
    return (
      <div className="doc-browser">
        <div className="doc-scanning-state">
          <h2>Documentation Engine</h2>
          <p className="doc-scan-status">
            Generating field descriptions for {selectedForGeneration.size} objects...
          </p>
          <div className="doc-progress-bar">
            <div
              className="doc-progress-fill"
              style={{ width: `${scanStatus.progress || 0}%` }}
            />
          </div>
          <p className="doc-progress-text">{scanStatus.progress || 0}% complete</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Whether we're in selection mode (checkboxes active)
  const hasSelection = selectedForGeneration.size > 0;

  // Full data dictionary view
  return (
    <div className="doc-browser">
      <div className="doc-header">
        <div className="doc-header-left">
          <h2>Documentation Engine</h2>
          <div className="doc-tabs">
            <button
              className={`doc-tab ${activeTab === 'objects' ? 'active' : ''}`}
              onClick={() => setActiveTab('objects')}
            >
              Objects & Fields ({objects.length})
            </button>
            <button
              className={`doc-tab ${activeTab === 'flows' ? 'active' : ''}`}
              onClick={() => { setActiveTab('flows'); if (flows.length > 0 && flowConnections.length === 0) fetchFlowConnections(); }}
            >
              Flows ({flows.length})
            </button>
            <button
              className={`doc-tab ${activeTab === 'pack' ? 'active' : ''}`}
              onClick={() => setActiveTab('pack')}
            >
              Context Pack
            </button>
            <button
              className={`doc-tab ${activeTab === 'automations' ? 'active' : ''}`}
              onClick={() => setActiveTab('automations')}
            >
              Automations
            </button>
            <button
              className={`doc-tab ${activeTab === 'graph' ? 'active' : ''}`}
              onClick={() => { setGraphTarget(null); setActiveTab('graph'); }}
            >
              Graph
            </button>
          </div>
        </div>
        {activeTab === 'objects' && (
          <div className="doc-header-actions">
            {hasSelection && (
              <button className="doc-generate-btn" onClick={generateForSelected} title="Generate AI descriptions for all selected objects and their fields">
                Generate for Selected ({selectedForGeneration.size})
              </button>
            )}
            {hasSelection && (() => {
              const deployable = objects.filter(o => selectedForGeneration.has(o.name) && (o.aiDescription || o.fields?.some(f => f.aiDescription)));
              return deployable.length > 0 ? (
                <button
                  className="doc-deploy-btn"
                  onClick={deploySelected}
                  disabled={deploying}
                  title="Push descriptions for selected objects to Salesforce"
                >
                  {deploying ? 'Deploying...' : `Deploy to Salesforce (${deployable.length})`}
                </button>
              ) : null;
            })()}
            {hasSelection && (
              <button className="doc-rescan-btn" onClick={() => { setSelectedForGeneration(new Set()); setObjectDescEdits({}); setReviewingObject(null); }} title="Deselect all objects">
                Clear Selection
              </button>
            )}
            <button className="doc-rescan-btn" onClick={exportDataDictionary} disabled={isExporting || objects.length === 0} title="Export all objects and fields as a CSV data dictionary">
              {isExporting ? 'Exporting...' : 'Export Data Dictionary'}
            </button>
            <button className="doc-rescan-btn" onClick={startScan} disabled={isScanning} title="Rescan all objects and fields in your org">
              Rescan
            </button>
          </div>
        )}
        {activeTab === 'flows' && (
          <div className="doc-header-actions">
            {hasFlowSelection && (
              <button className="doc-generate-btn" onClick={generateForSelectedFlows} title="Generate AI descriptions for selected flows">
                Generate for Selected ({selectedFlowsForGeneration.size})
              </button>
            )}
            {hasFlowSelection && (
              <button className="doc-rescan-btn" onClick={() => setSelectedFlowsForGeneration(new Set())} title="Deselect all flows">
                Clear Selection
              </button>
            )}
            <button className="doc-rescan-btn" onClick={startFlowScan} disabled={isFlowScanning} title={flows.length > 0 ? 'Rescan flows' : 'Scan flows from Salesforce'}>
              {isFlowScanning ? 'Scanning...' : flows.length > 0 ? 'Rescan Flows' : 'Scan Flows'}
            </button>
          </div>
        )}
      </div>

      {activeTab === 'objects' && (
        <>
      <div className="doc-search-bar">
        <input
          type="text"
          placeholder="Search objects by name, label, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="doc-search-input"
        />
      </div>

      <div className="doc-layout">
        {/* Sidebar */}
        <div className="doc-sidebar">
          {/* Compact selected list */}
          {hasSelection && (
            <div className="doc-sidebar-selected">
              <div className="doc-sidebar-selected-heading">Selected ({selectedForGeneration.size})</div>
              <div className="doc-sidebar-selected-list">
                {objects.filter(o => selectedForGeneration.has(o.name)).map(obj => (
                  <div
                    key={obj.name}
                    className="doc-sidebar-selected-item"
                    onClick={() => { setReviewingObject(obj.name); setSelectedObject(obj.name); fetchObjectDetail(obj.name); }}
                    title={`Review ${obj.label}`}
                  >
                    <span className="doc-sidebar-selected-label">{obj.label}</span>
                    <button
                      className="doc-sidebar-selected-remove"
                      onClick={(e) => { e.stopPropagation(); toggleSelectObject(obj.name); }}
                      title="Remove from selection"
                    >
                      &#10005;
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="doc-filters">
            <button
              className={`doc-filter-btn ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => setFilterType('all')}
              title="Show all objects"
            >
              All ({objects.length})
            </button>
            <button
              className={`doc-filter-btn ${filterType === 'standard' ? 'active' : ''}`}
              onClick={() => setFilterType('standard')}
              title="Show only standard Salesforce objects"
            >
              Standard ({objects.filter(o => !o.custom).length})
            </button>
            <button
              className={`doc-filter-btn ${filterType === 'custom' ? 'active' : ''}`}
              onClick={() => setFilterType('custom')}
              title="Show only custom objects"
            >
              Custom ({objects.filter(o => o.custom).length})
            </button>
            <button
              className={`doc-filter-btn ${filterType === 'ignored' ? 'active' : ''}`}
              onClick={() => { setFilterType('ignored'); fetchIgnoredObjects(); }}
              title="Show ignored objects"
            >
              Ignored ({ignoredCount})
            </button>
          </div>

          <div className="doc-object-list">
            {filterType === 'ignored' ? (
              <>
                {ignoredObjects.map(obj => (
                  <div key={obj.name} className="doc-object-item doc-ignored-item">
                    <div className="doc-object-item-content">
                      <div className="doc-object-item-name">
                        {obj.custom && <span className="doc-custom-badge">C</span>}
                        {obj.label}
                      </div>
                      <div className="doc-object-item-api">{obj.name}</div>
                    </div>
                    <button
                      className="doc-restore-btn"
                      onClick={() => unignoreObject(obj.name)}
                      title="Restore this object"
                    >
                      Restore
                    </button>
                  </div>
                ))}
                {ignoredObjects.length === 0 && (
                  <p className="doc-no-results">No ignored objects.</p>
                )}
              </>
            ) : (
              <>
                {filteredObjects.map(obj => (
                  <div
                    key={obj.name}
                    className={`doc-object-item ${selectedObject === obj.name && !hasSelection ? 'selected' : ''} ${selectedForGeneration.has(obj.name) ? 'checked' : ''}`}
                    onClick={() => { if (!hasSelection) handleSelectObject(obj); }}
                  >
                    <input
                      type="checkbox"
                      className="doc-object-checkbox"
                      checked={selectedForGeneration.has(obj.name)}
                      onChange={(e) => { e.stopPropagation(); toggleSelectObject(obj.name); }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="doc-object-item-content">
                      <div className="doc-object-item-name">
                        {obj.custom && <span className="doc-custom-badge">C</span>}
                        {obj.label}
                        {obj.aiSuggestedDescription && <span className="doc-suggestion-dot" title="Has AI suggestion"></span>}
                      </div>
                      <div className="doc-object-item-meta">
                        <span className="doc-object-item-api">{obj.name}</span>
                        {obj.scanStatus === 'complete' && (
                          <span
                            className={`doc-object-doc-pct ${obj.docPercent === 100 ? 'complete' : obj.docPercent > 0 ? 'partial' : ''}`}
                            title={`${obj.docPercent}% of fields documented`}
                          >
                            {obj.docPercent}%
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      className="doc-ignore-btn"
                      onClick={(e) => { e.stopPropagation(); ignoreObject(obj.name); }}
                      title="Ignore this object"
                    >
                      &#8856;
                    </button>
                  </div>
                ))}
                {filteredObjects.length === 0 && (
                  <p className="doc-no-results">No objects match your search.</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Detail pane */}
        <div className="doc-detail">
          {hasSelection && reviewingObject ? (
            /* Review detail view — drill into one selected object */
            (() => {
              const selectedObjectsList = objects.filter(o => selectedForGeneration.has(o.name));
              const reviewIndex = selectedObjectsList.findIndex(o => o.name === reviewingObject);
              const prevReviewObject = reviewIndex > 0 ? selectedObjectsList[reviewIndex - 1] : null;
              const nextReviewObject = reviewIndex < selectedObjectsList.length - 1 ? selectedObjectsList[reviewIndex + 1] : null;
              return (
                <div className="doc-detail-content">
                  <div className="doc-review-nav">
                    <button
                      className="doc-review-back"
                      onClick={() => setReviewingObject(null)}
                      title="Return to the selected objects list"
                    >
                      &#8592; Back to Selection
                    </button>
                    <div className="doc-review-stepper">
                      <button
                        className="doc-review-prev"
                        onClick={() => { setReviewingObject(prevReviewObject.name); setSelectedObject(prevReviewObject.name); fetchObjectDetail(prevReviewObject.name); }}
                        disabled={!prevReviewObject}
                        title={prevReviewObject ? `Previous: ${prevReviewObject.label}` : 'No previous object'}
                      >
                        &#8592; Prev
                      </button>
                      <span className="doc-review-position">{reviewIndex + 1} of {selectedObjectsList.length}</span>
                      <button
                        className="doc-review-next"
                        onClick={() => { setReviewingObject(nextReviewObject.name); setSelectedObject(nextReviewObject.name); fetchObjectDetail(nextReviewObject.name); }}
                        disabled={!nextReviewObject}
                        title={nextReviewObject ? `Next: ${nextReviewObject.label}` : 'No next object'}
                      >
                        Next &#8594;
                      </button>
                    </div>
                  </div>

                  {detailLoading ? (
                    <div className="doc-detail-loading">
                      <div className="lightsaber-loader"></div>
                      <p>Loading object details...</p>
                    </div>
                  ) : objectDetail ? (
                    <>
                      <div className="doc-detail-header">
                        <h3>{objectDetail.label}</h3>
                        <span className="doc-detail-api-name">{objectDetail.name}</span>
                        {objectDetail.custom && <span className="doc-custom-badge">Custom</span>}
                        {(objectDetail.aiDescription || objectDetail.fields?.some(f => f.aiDescription)) ? (
                          <button
                            className="doc-deploy-btn"
                            onClick={() => deployObject(objectDetail.name)}
                            disabled={deploying}
                            title={objectDetail.deployedToSfAt
                              ? `Last deployed: ${new Date(objectDetail.deployedToSfAt).toLocaleString()}`
                              : 'Push descriptions to Salesforce'}
                          >
                            {deploying ? 'Deploying...' : 'Push to Salesforce'}
                          </button>
                        ) : null}
                        {objectDetail.deployedToSfAt && (
                          <span className="doc-deploy-timestamp">
                            Deployed {new Date(objectDetail.deployedToSfAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      {/* AI Suggestion Banner */}
                      {objectDetail.aiSuggestedDescription && (
                        <div className="doc-suggestion-banner">
                          <div className="doc-suggestion-header">
                            <span className="doc-suggestion-label">AI Recommendation</span>
                            <div className="doc-suggestion-actions">
                              <button
                                className="doc-suggestion-accept"
                                onClick={acceptObjectSuggestion}
                                disabled={saving}
                                title="Accept this description"
                              >
                                Accept
                              </button>
                              <button
                                className="doc-suggestion-dismiss"
                                onClick={dismissObjectSuggestion}
                                disabled={saving}
                                title="Dismiss this suggestion"
                              >
                                Dismiss
                              </button>
                            </div>
                          </div>
                          <p className="doc-suggestion-text">{objectDetail.aiSuggestedDescription}</p>
                        </div>
                      )}

                      {/* Current Description */}
                      <div className="doc-ai-description">
                        <span className="doc-ai-label">Description</span>
                        {editingObjectDesc.active ? (
                          <div className="doc-edit-group">
                            <textarea
                              className="doc-edit-textarea"
                              value={editingObjectDesc.text}
                              onChange={(e) => setEditingObjectDesc(prev => ({ ...prev, text: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') setEditingObjectDesc({ active: false, text: '' });
                              }}
                              autoFocus
                              rows={3}
                            />
                            <div className="doc-edit-actions">
                              <button
                                className="doc-edit-save"
                                onClick={() => saveObjectDescription(editingObjectDesc.text)}
                                disabled={saving}
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                className="doc-edit-cancel"
                                onClick={() => setEditingObjectDesc({ active: false, text: '' })}
                                disabled={saving}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p
                            className="doc-editable"
                            onClick={() => setEditingObjectDesc({ active: true, text: objectDetail.aiDescription || '' })}
                            title="Click to edit"
                          >
                            {objectDetail.aiDescription || <span className="doc-no-desc">No description — click to add</span>}
                            <span className="doc-edit-icon">&#9998;</span>
                          </p>
                        )}
                      </div>

                      <div className="doc-detail-meta">
                        <div className="doc-meta-item">
                          <span className="doc-meta-label">Fields</span>
                          <span className="doc-meta-value">{objectDetail.fieldCount || objectDetail.fields?.length || 0}</span>
                        </div>
                        <div className="doc-meta-item">
                          <span className="doc-meta-label">Record Types</span>
                          <span className="doc-meta-value">{objectDetail.recordTypeCount || 0}</span>
                        </div>
                        <div className="doc-meta-item">
                          <span className="doc-meta-label">Child Relationships</span>
                          <span className="doc-meta-value">{objectDetail.childRelationshipCount || 0}</span>
                        </div>
                        <div className="doc-meta-item">
                          <span className="doc-meta-label">Key Prefix</span>
                          <span className="doc-meta-value">{objectDetail.keyPrefix || '—'}</span>
                        </div>
                      </div>

                      {/* Relationships */}
                      {objectDetail.childRelationships && objectDetail.childRelationships.length > 0 && (
                        <div className="doc-relationships">
                          <h4>Child Relationships</h4>
                          <div className="doc-rel-list">
                            {objectDetail.childRelationships.slice(0, 20).map((rel, i) => (
                              <span
                                key={i}
                                className="doc-rel-chip"
                                onClick={() => {
                                  const target = objects.find(o => o.name === rel.childSObject);
                                  if (target) handleSelectObject(target);
                                }}
                                style={{ cursor: objects.find(o => o.name === rel.childSObject) ? 'pointer' : 'default' }}
                              >
                                {rel.childSObject}
                              </span>
                            ))}
                            {objectDetail.childRelationships.length > 20 && (
                              <span className="doc-rel-more">+{objectDetail.childRelationships.length - 20} more</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Fields table */}
                      <div className="doc-fields-section">
                        <div className="doc-fields-header">
                          <h4>Fields ({objectDetail.fields?.length || 0})</h4>
                          <div className="doc-fields-header-actions">
                            {objectDetail.fields?.some(f => f.aiSuggestedDescription) && (
                              <button
                                className="doc-accept-all-btn"
                                onClick={acceptAllSuggestions}
                                disabled={acceptingAll || saving}
                                title="Accept all pending AI suggestions for this object's fields"
                              >
                                {acceptingAll ? 'Accepting...' : `Accept All Suggestions (${objectDetail.fields.filter(f => f.aiSuggestedDescription).length})`}
                              </button>
                            )}
                            <input
                              type="text"
                              placeholder="Filter fields..."
                              value={fieldSearch}
                              onChange={(e) => setFieldSearch(e.target.value)}
                              className="doc-field-search"
                            />
                          </div>
                        </div>
                        <div className="doc-fields-table-wrapper">
                          <table className="doc-fields-table">
                            <thead>
                              <tr>
                                <th className="doc-th-field">Field</th>
                                <th className="doc-th-desc">Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredFields.map(field => (
                                <tr key={field.name} className={field.custom ? 'doc-field-custom' : ''}>
                                  <td className="doc-field-info">
                                    <div className="doc-field-label">
                                      {field.custom && <span className="doc-custom-dot"></span>}
                                      {field.label}
                                    </div>
                                    <div className="doc-field-meta">
                                      <span className="doc-field-api">{field.name}</span>
                                      <span className="doc-field-type">{field.type}</span>
                                      {field.referenceTo && field.referenceTo.length > 0 && (
                                        <span className="doc-field-ref">→ {field.referenceTo.join(', ')}</span>
                                      )}
                                      {field.calculatedFormula && (
                                        <span
                                          className="doc-field-formula-badge"
                                          onClick={() => toggleFormula(field.name)}
                                          title="Show formula"
                                        >
                                          fx {expandedFormulas.has(field.name) ? '▾' : '▸'}
                                        </span>
                                      )}
                                      {field.sfCreatedDate && (
                                        <span
                                          className="doc-field-created-date"
                                          title={new Date(field.sfCreatedDate).toLocaleString()}
                                        >
                                          {new Date(field.sfCreatedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </span>
                                      )}
                                    </div>
                                    {field.calculatedFormula && expandedFormulas.has(field.name) && (
                                      <div className="doc-field-formula-body">{field.calculatedFormula}</div>
                                    )}
                                  </td>
                                  <td className="doc-field-desc">
                                    {field.aiSuggestedDescription && (
                                      <div className="doc-field-suggestion">
                                        <span className="doc-field-suggestion-text">{field.aiSuggestedDescription}</span>
                                        <span className="doc-field-suggestion-actions">
                                          <button
                                            className="doc-field-accept"
                                            onClick={() => acceptFieldSuggestion(field.name)}
                                            disabled={saving}
                                            title="Accept"
                                          >
                                            &#10003;
                                          </button>
                                          <button
                                            className="doc-field-dismiss"
                                            onClick={() => dismissFieldSuggestion(field.name)}
                                            disabled={saving}
                                            title="Dismiss"
                                          >
                                            &#10005;
                                          </button>
                                        </span>
                                      </div>
                                    )}
                                    {editingFieldDesc.fieldName === field.name ? (
                                      <div className="doc-edit-field-group">
                                        <textarea
                                          className="doc-edit-field-textarea"
                                          value={editingFieldDesc.text}
                                          onChange={(e) => setEditingFieldDesc(prev => ({ ...prev, text: e.target.value }))}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Escape') setEditingFieldDesc({ fieldName: null, text: '' });
                                          }}
                                          autoFocus
                                          disabled={saving}
                                          rows={3}
                                        />
                                        <div className="doc-edit-field-actions">
                                          <button
                                            className="doc-edit-save"
                                            onClick={() => saveFieldDescription(field.name, editingFieldDesc.text)}
                                            disabled={saving}
                                          >
                                            {saving ? 'Saving...' : 'Save'}
                                          </button>
                                          <button
                                            className="doc-edit-cancel"
                                            onClick={() => setEditingFieldDesc({ fieldName: null, text: '' })}
                                            disabled={saving}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <span
                                        className="doc-editable"
                                        onClick={() => setEditingFieldDesc({ fieldName: field.name, text: field.aiDescription || '' })}
                                        title="Click to edit"
                                      >
                                        {field.aiDescription || (!field.aiSuggestedDescription && <span className="doc-no-desc">—</span>)}
                                        <span className="doc-edit-icon">&#9998;</span>
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                              {filteredFields.length === 0 && (
                                <tr>
                                  <td colSpan="2" className="doc-no-results">No fields match your filter.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })()
          ) : hasSelection ? (
            <div className="doc-detail-content">
              {/* Org Context */}
              <div className="doc-org-context">
                <div className="doc-org-context-header">
                  <span className="doc-ai-label">Org Context</span>
                  <span className="doc-org-context-hint">
                    Describe your business, org purpose, or naming conventions to help AI generate better descriptions.
                  </span>
                </div>
                <textarea
                  className="doc-edit-textarea doc-auto-resize"
                  value={orgContextNote}
                  onChange={(e) => setOrgContextNote(e.target.value)}
                  onBlur={saveOrgContext}
                  placeholder="e.g., B2B SaaS company. 'DFX__' prefix = deal flow extension objects..."
                  rows={1}
                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                  onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                />
              </div>

              {/* Selected objects description list */}
              <h4 className="doc-selected-heading">Selected Objects ({selectedForGeneration.size})</h4>
              <div className="doc-selected-list">
                {objects.filter(o => selectedForGeneration.has(o.name)).map(obj => (
                  <div key={obj.name} className="doc-selected-item">
                    <div className="doc-selected-item-header">
                      <div className="doc-selected-item-name">
                        {obj.custom && <span className="doc-custom-badge">C</span>}
                        <strong>{obj.label}</strong>
                        <span className="doc-selected-api-name">{obj.name}</span>
                        {obj.aiSuggestedDescription && <span className="doc-suggestion-dot" title="Has AI suggestion"></span>}
                      </div>
                      <div className="doc-selected-item-actions">
                        <button
                          className="doc-selected-view"
                          onClick={() => { setReviewingObject(obj.name); setSelectedObject(obj.name); fetchObjectDetail(obj.name); }}
                          title="View full detail"
                        >
                          View
                        </button>
                        <button
                          className="doc-selected-remove"
                          onClick={() => toggleSelectObject(obj.name)}
                          title="Remove from selection"
                        >
                          &#10005;
                        </button>
                      </div>
                    </div>

                    {/* AI Suggestion */}
                    {obj.aiSuggestedDescription && (
                      <div className="doc-selected-suggestion">
                        <div className="doc-selected-suggestion-header">
                          <span className="doc-suggestion-label">AI Suggestion</span>
                          <div className="doc-suggestion-actions">
                            <button
                              className="doc-suggestion-accept"
                              onClick={async () => {
                                await fetch(`${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${obj.name}/suggestion/accept`, { method: 'POST', headers: getAuthHeaders() });
                                await fetchObjects();
                                setObjectDescEdits(prev => ({ ...prev, [obj.name]: obj.aiSuggestedDescription }));
                              }}
                              disabled={saving}
                              title="Use this suggestion as the object description"
                            >Accept</button>
                            <button
                              className="doc-suggestion-dismiss"
                              onClick={async () => {
                                await fetch(`${API_ENDPOINTS.DOCUMENTATION_OBJECTS}/${obj.name}/suggestion/dismiss`, { method: 'POST', headers: getAuthHeaders() });
                                await fetchObjects();
                              }}
                              disabled={saving}
                              title="Discard this suggestion"
                            >Dismiss</button>
                          </div>
                        </div>
                        <p className="doc-suggestion-text">{obj.aiSuggestedDescription}</p>
                      </div>
                    )}

                    {/* Editable description */}
                    <label className="doc-desc-label">Current Description</label>
                    <textarea
                      className="doc-selected-desc-textarea"
                      value={objectDescEdits[obj.name] !== undefined ? objectDescEdits[obj.name] : (obj.aiDescription || '')}
                      onChange={(e) => handleDescriptionEdit(obj.name, e.target.value)}
                      onBlur={() => handleDescriptionBlur(obj.name)}
                      placeholder="Describe this object's purpose in your org..."
                      rows={1}
                      ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : !selectedObject ? (
            <div className="doc-detail-empty">
              <p>Select an object to view details, or check boxes to select objects for AI generation.</p>
            </div>
          ) : detailLoading ? (
            <div className="doc-detail-loading">
              <div className="lightsaber-loader"></div>
              <p>Loading object details...</p>
            </div>
          ) : objectDetail ? (
            <div className="doc-detail-content">
              <div className="doc-detail-header">
                <h3>{objectDetail.label}</h3>
                <span className="doc-detail-api-name">{objectDetail.name}</span>
                {objectDetail.custom && <span className="doc-custom-badge">Custom</span>}
                {objectDetail.custom && (objectDetail.aiDescription || objectDetail.fields?.some(f => f.aiDescription)) ? (
                  <button
                    className="doc-deploy-btn"
                    onClick={() => deployObject(objectDetail.name)}
                    disabled={deploying}
                    title={objectDetail.deployedToSfAt
                      ? `Last deployed: ${new Date(objectDetail.deployedToSfAt).toLocaleString()}`
                      : 'Push descriptions to Salesforce'}
                  >
                    {deploying ? 'Deploying...' : 'Push to Salesforce'}
                  </button>
                ) : !objectDetail.custom ? (
                  <span className="doc-deploy-disabled" title="Standard objects cannot have descriptions deployed">
                    Deploy N/A
                  </span>
                ) : null}
                {objectDetail.deployedToSfAt && (
                  <span className="doc-deploy-timestamp">
                    Deployed {new Date(objectDetail.deployedToSfAt).toLocaleDateString()}
                  </span>
                )}
              </div>

              {/* AI Suggestion Banner */}
              {objectDetail.aiSuggestedDescription && (
                <div className="doc-suggestion-banner">
                  <div className="doc-suggestion-header">
                    <span className="doc-suggestion-label">AI Recommendation</span>
                    <div className="doc-suggestion-actions">
                      <button
                        className="doc-suggestion-accept"
                        onClick={acceptObjectSuggestion}
                        disabled={saving}
                        title="Accept this description"
                      >
                        Accept
                      </button>
                      <button
                        className="doc-suggestion-dismiss"
                        onClick={dismissObjectSuggestion}
                        disabled={saving}
                        title="Dismiss this suggestion"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                  <p className="doc-suggestion-text">{objectDetail.aiSuggestedDescription}</p>
                </div>
              )}

              {/* Current Description */}
              <div className="doc-ai-description">
                <span className="doc-ai-label">Description</span>
                {editingObjectDesc.active ? (
                  <div className="doc-edit-group">
                    <textarea
                      className="doc-edit-textarea"
                      value={editingObjectDesc.text}
                      onChange={(e) => setEditingObjectDesc(prev => ({ ...prev, text: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setEditingObjectDesc({ active: false, text: '' });
                      }}
                      autoFocus
                      rows={3}
                    />
                    <div className="doc-edit-actions">
                      <button
                        className="doc-edit-save"
                        onClick={() => saveObjectDescription(editingObjectDesc.text)}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="doc-edit-cancel"
                        onClick={() => setEditingObjectDesc({ active: false, text: '' })}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p
                    className="doc-editable"
                    onClick={() => setEditingObjectDesc({ active: true, text: objectDetail.aiDescription || '' })}
                    title="Click to edit"
                  >
                    {objectDetail.aiDescription || <span className="doc-no-desc">No description — click to add</span>}
                    <span className="doc-edit-icon">&#9998;</span>
                  </p>
                )}
              </div>

              <div className="doc-detail-meta">
                <div className="doc-meta-item">
                  <span className="doc-meta-label">Fields</span>
                  <span className="doc-meta-value">{objectDetail.fieldCount || objectDetail.fields?.length || 0}</span>
                </div>
                <div className="doc-meta-item">
                  <span className="doc-meta-label">Record Types</span>
                  <span className="doc-meta-value">{objectDetail.recordTypeCount || 0}</span>
                </div>
                <div className="doc-meta-item">
                  <span className="doc-meta-label">Child Relationships</span>
                  <span className="doc-meta-value">{objectDetail.childRelationshipCount || 0}</span>
                </div>
                <div className="doc-meta-item">
                  <span className="doc-meta-label">Key Prefix</span>
                  <span className="doc-meta-value">{objectDetail.keyPrefix || '—'}</span>
                </div>
              </div>

              {/* Relationships */}
              {objectDetail.childRelationships && objectDetail.childRelationships.length > 0 && (
                <div className="doc-relationships">
                  <h4>Child Relationships</h4>
                  <div className="doc-rel-list">
                    {objectDetail.childRelationships.slice(0, 20).map((rel, i) => (
                      <span
                        key={i}
                        className="doc-rel-chip"
                        onClick={() => {
                          const target = objects.find(o => o.name === rel.childSObject);
                          if (target) handleSelectObject(target);
                        }}
                        style={{ cursor: objects.find(o => o.name === rel.childSObject) ? 'pointer' : 'default' }}
                      >
                        {rel.childSObject}
                      </span>
                    ))}
                    {objectDetail.childRelationships.length > 20 && (
                      <span className="doc-rel-more">+{objectDetail.childRelationships.length - 20} more</span>
                    )}
                  </div>
                </div>
              )}

              {/* Flows touching this object */}
              {flowsForObject.length > 0 && (
                <div className="doc-relationships">
                  <h4>Flows Touching This Object ({flowsForObject.length})</h4>
                  <div className="doc-rel-list">
                    {flowsForObject.map((flow, i) => (
                      <span
                        key={i}
                        className="doc-rel-chip"
                        onClick={() => { setActiveTab('flows'); setSelectedFlow(flow.apiName); fetchFlowDetail(flow.apiName); }}
                        style={{ cursor: 'pointer' }}
                        title={`Operations: ${flow.operations.join(', ')}`}
                      >
                        {flow.label}
                        <span className="doc-flow-ops-mini">{flow.operations.map(op => op[0].toUpperCase()).join('')}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Fields table */}
              <div className="doc-fields-section">
                <div className="doc-fields-header">
                  <h4>Fields ({objectDetail.fields?.length || 0})</h4>
                  <div className="doc-fields-header-actions">
                    {objectDetail.fields?.some(f => f.aiSuggestedDescription) && (
                      <button
                        className="doc-accept-all-btn"
                        onClick={acceptAllSuggestions}
                        disabled={acceptingAll || saving}
                      >
                        {acceptingAll ? 'Accepting...' : `Accept All Suggestions (${objectDetail.fields.filter(f => f.aiSuggestedDescription).length})`}
                      </button>
                    )}
                    <input
                      type="text"
                      placeholder="Filter fields..."
                      value={fieldSearch}
                      onChange={(e) => setFieldSearch(e.target.value)}
                      className="doc-field-search"
                    />
                  </div>
                </div>
                <div className="doc-fields-table-wrapper">
                  <table className="doc-fields-table">
                    <thead>
                      <tr>
                        <th className="doc-th-field">Field</th>
                        <th className="doc-th-desc">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFields.map(field => (
                        <tr key={field.name} className={field.custom ? 'doc-field-custom' : ''}>
                          <td className="doc-field-info">
                            <div className="doc-field-label">
                              {field.custom && <span className="doc-custom-dot"></span>}
                              {field.label}
                            </div>
                            <div className="doc-field-meta">
                              <span className="doc-field-api">{field.name}</span>
                              <span className="doc-field-type">{field.type}</span>
                              {field.referenceTo && field.referenceTo.length > 0 && (
                                <span className="doc-field-ref">→ {field.referenceTo.join(', ')}</span>
                              )}
                              {field.calculatedFormula && (
                                <span
                                  className="doc-field-formula-badge"
                                  onClick={() => toggleFormula(field.name)}
                                  title="Show formula"
                                >
                                  fx {expandedFormulas.has(field.name) ? '▾' : '▸'}
                                </span>
                              )}
                              {field.sfCreatedDate && (
                                <span
                                  className="doc-field-created-date"
                                  title={new Date(field.sfCreatedDate).toLocaleString()}
                                >
                                  {new Date(field.sfCreatedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                            {field.calculatedFormula && expandedFormulas.has(field.name) && (
                              <div className="doc-field-formula-body">{field.calculatedFormula}</div>
                            )}
                          </td>
                          <td className="doc-field-desc">
                            {field.aiSuggestedDescription && (
                              <div className="doc-field-suggestion">
                                <span className="doc-field-suggestion-text">{field.aiSuggestedDescription}</span>
                                <span className="doc-field-suggestion-actions">
                                  <button
                                    className="doc-field-accept"
                                    onClick={() => acceptFieldSuggestion(field.name)}
                                    disabled={saving}
                                    title="Accept"
                                  >
                                    &#10003;
                                  </button>
                                  <button
                                    className="doc-field-dismiss"
                                    onClick={() => dismissFieldSuggestion(field.name)}
                                    disabled={saving}
                                    title="Dismiss"
                                  >
                                    &#10005;
                                  </button>
                                </span>
                              </div>
                            )}
                            {editingFieldDesc.fieldName === field.name ? (
                              <div className="doc-edit-field-group">
                                <textarea
                                  className="doc-edit-field-textarea"
                                  value={editingFieldDesc.text}
                                  onChange={(e) => setEditingFieldDesc(prev => ({ ...prev, text: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') setEditingFieldDesc({ fieldName: null, text: '' });
                                  }}
                                  autoFocus
                                  disabled={saving}
                                  rows={3}
                                />
                                <div className="doc-edit-field-actions">
                                  <button
                                    className="doc-edit-save"
                                    onClick={() => saveFieldDescription(field.name, editingFieldDesc.text)}
                                    disabled={saving}
                                  >
                                    {saving ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    className="doc-edit-cancel"
                                    onClick={() => setEditingFieldDesc({ fieldName: null, text: '' })}
                                    disabled={saving}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <span
                                className="doc-editable"
                                onClick={() => setEditingFieldDesc({ fieldName: field.name, text: field.aiDescription || '' })}
                                title="Click to edit"
                              >
                                {field.aiDescription || (!field.aiSuggestedDescription && <span className="doc-no-desc">—</span>)}
                                <span className="doc-edit-icon">&#9998;</span>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {filteredFields.length === 0 && (
                        <tr>
                          <td colSpan="2" className="doc-no-results">No fields match your filter.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {deployResult && (
        <div className={`doc-deploy-result-banner ${deployResult.errors?.length > 0 ? 'has-errors' : ''}`}>
          <div className="doc-deploy-result-content">
            <span>
              Deployed: {deployResult.objectsDeployed ?? (deployResult.objectDeployed ? 1 : 0)} object(s),{' '}
              {deployResult.fieldsDeployed} field(s)
              {deployResult.errors?.length > 0 && ` — ${deployResult.errors.length} error(s)`}
            </span>
            {deployResult.errors?.length > 0 && (
              <div className="doc-deploy-error-list">
                {deployResult.errors.map((err, i) => (
                  <div key={i} className="doc-deploy-error-item">
                    {err.name}: {err.error}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="doc-deploy-result-close" onClick={() => setDeployResult(null)} title="Dismiss">&#10005;</button>
        </div>
      )}
        </>
      )}

      {/* === FLOWS TAB === */}
      {activeTab === 'flows' && (
        <>
          {isFlowScanning && (
            <div className="doc-scanning-state" style={{ minHeight: '30vh' }}>
              <p className="doc-scan-status">Scanning flows... {flowScanProgress.scanned} of {flowScanProgress.total || '?'}</p>
              <div className="doc-progress-bar">
                <div className="doc-progress-fill" style={{ width: `${flowScanProgress.progress || 0}%` }} />
              </div>
              <p className="doc-progress-text">{flowScanProgress.progress || 0}% complete</p>
            </div>
          )}

          {isFlowGenerating && (
            <div className="doc-scanning-state" style={{ minHeight: '30vh' }}>
              <p className="doc-scan-status">Generating flow descriptions for {selectedFlowsForGeneration.size} flows...</p>
              <div className="doc-progress-bar">
                <div className="doc-progress-fill" style={{ width: `${flowGenerateProgress || 0}%` }} />
              </div>
              <p className="doc-progress-text">{flowGenerateProgress || 0}% complete</p>
            </div>
          )}

          {!isFlowScanning && !isFlowGenerating && flows.length === 0 && flowScanStatus !== 'scanning' && (
            <div className="doc-detail-empty" style={{ textAlign: 'center', padding: '3rem' }}>
              <p>No flows scanned yet. Click "Scan Flows" to discover and document Salesforce Flows in your org.</p>
              {objects.length === 0 && (
                <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  Tip: Scan Objects & Fields first for richer AI context.
                </p>
              )}
            </div>
          )}

          {!isFlowScanning && !isFlowGenerating && flows.length > 0 && (
            <>
              <div className="doc-search-bar">
                <input
                  type="text"
                  placeholder="Search flows by name, trigger object, or description..."
                  value={flowSearchQuery}
                  onChange={(e) => setFlowSearchQuery(e.target.value)}
                  className="doc-search-input"
                />
              </div>

              <div className="doc-layout">
                {/* Flow Sidebar */}
                <div className="doc-sidebar">
                  <div className="doc-filters">
                    {[
                      { key: 'all', label: `All (${flows.length})` },
                      { key: 'record-triggered', label: `Record (${flows.filter(f => f.triggerObject).length})` },
                      { key: 'screen', label: `Screen (${flows.filter(f => f.processType === 'Flow').length})` },
                      { key: 'scheduled', label: `Scheduled (${flows.filter(f => f.triggerType === 'Scheduled').length})` },
                      { key: 'auto', label: `Auto (${flows.filter(f => f.processType === 'AutoLaunchedFlow' && !f.triggerObject).length})` },
                    ].map(ft => (
                      <button
                        key={ft.key}
                        className={`doc-filter-btn ${flowFilterType === ft.key ? 'active' : ''}`}
                        onClick={() => setFlowFilterType(ft.key)}
                      >
                        {ft.label}
                      </button>
                    ))}
                  </div>

                  <div className="doc-object-list">
                    {filteredFlows.map(flow => (
                      <div
                        key={flow.apiName}
                        className={`doc-object-item ${selectedFlow === flow.apiName && !hasFlowSelection ? 'selected' : ''} ${selectedFlowsForGeneration.has(flow.apiName) ? 'checked' : ''}`}
                        onClick={() => { if (!hasFlowSelection) { setSelectedFlow(flow.apiName); fetchFlowDetail(flow.apiName); } }}
                      >
                        <input
                          type="checkbox"
                          className="doc-object-checkbox"
                          checked={selectedFlowsForGeneration.has(flow.apiName)}
                          onChange={(e) => { e.stopPropagation(); toggleSelectFlow(flow.apiName); }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="doc-object-item-content">
                          <div className="doc-object-item-name">
                            <span className={`doc-flow-type-badge doc-flow-type-${(flow.processType || '').toLowerCase()}`}>
                              {flow.triggerObject ? 'RT' : flow.processType === 'Flow' ? 'SC' : flow.triggerType === 'Scheduled' ? 'SH' : 'AL'}
                            </span>
                            {flow.label}
                            {flow.aiSuggestedDescription && <span className="doc-suggestion-dot" title="Has AI suggestion"></span>}
                          </div>
                          <div className="doc-object-item-api">
                            {flow.apiName}
                            {flow.triggerObject && <span className="doc-flow-trigger-chip">{flow.triggerObject}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredFlows.length === 0 && (
                      <p className="doc-no-results">No flows match your search.</p>
                    )}
                  </div>
                </div>

                {/* Flow Detail Pane */}
                <div className="doc-detail">
                  {!selectedFlow && !hasFlowSelection ? (
                    <div className="doc-detail-empty">
                      <p>Select a flow to view details, or check boxes to select flows for AI generation.</p>
                    </div>
                  ) : flowDetailLoading ? (
                    <div className="doc-detail-loading">
                      <div className="lightsaber-loader"></div>
                      <p>Loading flow details...</p>
                    </div>
                  ) : flowDetail ? (
                    <div className="doc-detail-content">
                      <div className="doc-detail-header">
                        <h3>{flowDetail.label}</h3>
                        <span className="doc-detail-api-name">{flowDetail.apiName}</span>
                        <span className={`doc-flow-type-badge doc-flow-type-${(flowDetail.processType || '').toLowerCase()}`}>
                          {flowDetail.processType || 'Unknown'}
                        </span>
                        <button
                          className="doc-rescan-btn"
                          onClick={() => {
                            setGraphTarget('flow:' + selectedFlow);
                            setActiveTab('graph');
                          }}
                          title="Open this flow's neighborhood in Graph Explorer"
                        >
                          View in Graph Explorer
                        </button>
                      </div>

                      {/* AI Suggestion Banner */}
                      {flowDetail.aiSuggestedDescription && (
                        <div className="doc-suggestion-banner">
                          <div className="doc-suggestion-header">
                            <span className="doc-suggestion-label">AI Recommendation</span>
                            <div className="doc-suggestion-actions">
                              <button className="doc-suggestion-accept" onClick={acceptFlowSuggestion} disabled={saving}>Accept</button>
                              <button className="doc-suggestion-dismiss" onClick={dismissFlowSuggestion} disabled={saving}>Dismiss</button>
                            </div>
                          </div>
                          <p className="doc-suggestion-text">{flowDetail.aiSuggestedDescription}</p>
                        </div>
                      )}

                      {/* Description */}
                      <div className="doc-ai-description">
                        <span className="doc-ai-label">Description</span>
                        {editingFlowDesc.active ? (
                          <div className="doc-edit-group">
                            <textarea
                              className="doc-edit-textarea"
                              value={editingFlowDesc.text}
                              onChange={(e) => setEditingFlowDesc(prev => ({ ...prev, text: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Escape') setEditingFlowDesc({ active: false, text: '' }); }}
                              autoFocus
                              rows={3}
                            />
                            <div className="doc-edit-actions">
                              <button className="doc-edit-save" onClick={() => saveFlowDescription(editingFlowDesc.text)} disabled={saving}>
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button className="doc-edit-cancel" onClick={() => setEditingFlowDesc({ active: false, text: '' })} disabled={saving}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <p
                            className="doc-editable"
                            onClick={() => setEditingFlowDesc({ active: true, text: flowDetail.aiDescription || '' })}
                            title="Click to edit"
                          >
                            {flowDetail.aiDescription || <span className="doc-no-desc">No description — click to add</span>}
                            <span className="doc-edit-icon">&#9998;</span>
                          </p>
                        )}
                      </div>

                      {/* Trigger Info */}
                      {flowDetail.triggerObject && (
                        <div className="doc-detail-meta">
                          <div className="doc-meta-item">
                            <span className="doc-meta-label">Trigger Object</span>
                            <span
                              className="doc-meta-value doc-flow-object-link"
                              onClick={() => {
                                const target = objects.find(o => o.name === flowDetail.triggerObject);
                                if (target) { setActiveTab('objects'); handleSelectObject(target); }
                              }}
                              style={{ cursor: objects.find(o => o.name === flowDetail.triggerObject) ? 'pointer' : 'default' }}
                            >
                              {flowDetail.triggerObject}
                            </span>
                          </div>
                          <div className="doc-meta-item">
                            <span className="doc-meta-label">Trigger Type</span>
                            <span className="doc-meta-value">{flowDetail.triggerType || '—'}</span>
                          </div>
                          <div className="doc-meta-item">
                            <span className="doc-meta-label">Record Trigger</span>
                            <span className="doc-meta-value">{flowDetail.triggerRecordType || '—'}</span>
                          </div>
                          <div className="doc-meta-item">
                            <span className="doc-meta-label">Elements</span>
                            <span className="doc-meta-value">{flowDetail.elementCount || 0}</span>
                          </div>
                        </div>
                      )}

                      {/* Element Summary */}
                      {flowDetail.parsedSummary?.elementCounts && (
                        <div className="doc-flow-elements">
                          <h4>Element Summary</h4>
                          <div className="doc-flow-element-grid">
                            {Object.entries(flowDetail.parsedSummary.elementCounts)
                              .filter(([, count]) => count > 0)
                              .map(([type, count]) => (
                                <div key={type} className="doc-flow-element-item">
                                  <span className="doc-flow-element-type">{type.replace(/([A-Z])/g, ' $1').replace(/^record /i, '').trim()}</span>
                                  <span className="doc-flow-element-count">{count}</span>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )}

                      {/* Field Interactions */}
                      {flowDetail.fieldRefs && flowDetail.fieldRefs.length > 0 && (
                        <div className="doc-flow-field-refs">
                          <h4>Field Interactions</h4>
                          {(() => {
                            const grouped = {};
                            for (const ref of flowDetail.fieldRefs) {
                              if (!grouped[ref.objectName]) grouped[ref.objectName] = [];
                              grouped[ref.objectName].push(ref);
                            }
                            return Object.entries(grouped).map(([objName, refs]) => (
                              <div key={objName} className="doc-flow-ref-group">
                                <div
                                  className="doc-flow-ref-object"
                                  onClick={() => {
                                    const target = objects.find(o => o.name === objName);
                                    if (target) { setActiveTab('objects'); handleSelectObject(target); }
                                  }}
                                  style={{ cursor: objects.find(o => o.name === objName) ? 'pointer' : 'default' }}
                                >
                                  {objName}
                                </div>
                                <div className="doc-flow-ref-fields">
                                  {refs.map((ref, i) => (
                                    <span key={i} className={`doc-flow-ref-chip doc-flow-op-${ref.operation}`}>
                                      {ref.fieldName || '(record)'}
                                      <span className="doc-flow-ref-op">{ref.operation}</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}

                      {/* Connections */}
                      {flowDetail.connections && (flowDetail.connections.outgoing.length > 0 || flowDetail.connections.incoming.length > 0) && (
                        <div className="doc-flow-connections">
                          <h4>Connections</h4>
                          {flowDetail.connections.outgoing.length > 0 && (
                            <div className="doc-flow-conn-section">
                              <span className="doc-flow-conn-label">Calls:</span>
                              <div className="doc-rel-list">
                                {flowDetail.connections.outgoing.map((conn, i) => (
                                  <span
                                    key={i}
                                    className={`doc-rel-chip ${conn.inferred ? 'doc-rel-inferred' : ''}`}
                                    onClick={() => { setSelectedFlow(conn.targetFlow); fetchFlowDetail(conn.targetFlow); }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {conn.targetFlow}
                                    {conn.inferred && <span className="doc-flow-conn-inferred-label">inferred</span>}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {flowDetail.connections.incoming.length > 0 && (
                            <div className="doc-flow-conn-section">
                              <span className="doc-flow-conn-label">Called by:</span>
                              <div className="doc-rel-list">
                                {flowDetail.connections.incoming.map((conn, i) => (
                                  <span
                                    key={i}
                                    className={`doc-rel-chip ${conn.inferred ? 'doc-rel-inferred' : ''}`}
                                    onClick={() => { setSelectedFlow(conn.sourceFlow); fetchFlowDetail(conn.sourceFlow); }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {conn.sourceFlow}
                                    {conn.inferred && <span className="doc-flow-conn-inferred-label">inferred</span>}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'pack' && <ContextPackPanel getAuthHeaders={getAuthHeaders} />}

      {activeTab === 'automations' && <AutomationInventory getAuthHeaders={getAuthHeaders} />}

      {activeTab === 'graph' && <GraphExplorer getAuthHeaders={getAuthHeaders} initialNode={graphTarget} />}

      {error && <div className="doc-error-banner">{error}</div>}
      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .doc-browser {
    padding: 1.5rem;
    max-width: 1400px;
    margin: 0 auto;
    min-height: 70vh;
  }

  .doc-loading,
  .doc-empty-state,
  .doc-scanning-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
    text-align: center;
    gap: 1rem;
  }

  .doc-empty-icon {
    font-size: 4rem;
  }

  .doc-empty-state h2,
  .doc-scanning-state h2 {
    font-family: var(--font-display);
    color: var(--force-cyan);
    font-size: 1.8rem;
    margin: 0;
  }

  .doc-empty-state p {
    color: var(--gray-400);
    max-width: 480px;
    line-height: 1.6;
  }

  .doc-scan-btn {
    background: linear-gradient(135deg, var(--jedi-blue), var(--force-cyan));
    color: #fff;
    border: none;
    padding: 0.8rem 2rem;
    border-radius: 8px;
    font-family: var(--font-display);
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .doc-scan-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 212, 255, 0.4);
  }

  .doc-scan-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .doc-progress-bar {
    width: 100%;
    max-width: 400px;
    height: 8px;
    background: var(--gray-800);
    border-radius: 4px;
    overflow: hidden;
  }

  .doc-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--jedi-blue), var(--force-cyan));
    border-radius: 4px;
    transition: width 0.5s ease;
  }

  .doc-progress-text {
    color: var(--gray-400);
    font-size: 0.85rem;
  }

  .doc-scan-status {
    color: var(--force-cyan);
    font-family: var(--font-body);
  }

  /* Header */
  .doc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .doc-header-left {
    display: flex;
    align-items: baseline;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .doc-header h2 {
    font-family: var(--font-display);
    color: var(--force-cyan);
    font-size: 1.5rem;
    margin: 0;
  }

  .doc-object-count {
    color: var(--gray-400);
    font-size: 0.85rem;
  }

  .doc-suggestion-count {
    color: #4caf50;
    font-size: 0.8rem;
    font-weight: 500;
    background: rgba(34, 139, 34, 0.1);
    padding: 0.15rem 0.6rem;
    border-radius: 12px;
    border: 1px solid rgba(34, 139, 34, 0.25);
  }

  .doc-rescan-btn {
    background: transparent;
    color: var(--force-cyan);
    border: 1px solid var(--force-cyan);
    padding: 0.4rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
  }

  .doc-rescan-btn:hover:not(:disabled) {
    background: rgba(0, 212, 255, 0.1);
  }

  /* Search */
  .doc-search-bar {
    margin-bottom: 1rem;
  }

  .doc-search-input {
    width: 100%;
    padding: 0.7rem 1rem;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--gray-700);
    border-radius: 8px;
    color: var(--star-white);
    font-family: var(--font-body);
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.2s;
  }

  .doc-search-input:focus {
    border-color: var(--force-cyan);
  }

  .doc-search-input::placeholder {
    color: var(--gray-500);
  }

  /* Layout */
  .doc-layout {
    display: flex;
    gap: 1rem;
    min-height: 60vh;
  }

  /* Sidebar */
  .doc-sidebar {
    width: 280px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .doc-filters {
    display: flex;
    gap: 0.25rem;
  }

  .doc-filter-btn {
    flex: 1;
    padding: 0.4rem 0.5rem;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--gray-700);
    color: var(--gray-400);
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.75rem;
    transition: all 0.2s;
  }

  .doc-filter-btn.active {
    background: rgba(0, 212, 255, 0.15);
    border-color: var(--force-cyan);
    color: var(--force-cyan);
  }

  .doc-object-list {
    flex: 1;
    overflow-y: auto;
    max-height: calc(100vh - 320px);
    border: 1px solid var(--gray-700);
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.2);
  }

  .doc-object-item {
    padding: 0.6rem 0.8rem;
    cursor: pointer;
    border-bottom: 1px solid var(--gray-800);
    transition: background 0.15s;
  }

  .doc-object-item:hover {
    background: rgba(0, 212, 255, 0.05);
  }

  .doc-object-item.selected {
    background: rgba(0, 212, 255, 0.12);
    border-left: 3px solid var(--force-cyan);
  }

  .doc-object-item-name {
    color: var(--star-white);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .doc-object-item-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .doc-object-item-api {
    color: var(--gray-500);
    font-size: 0.75rem;
    font-family: var(--font-body);
  }

  .doc-object-doc-pct {
    font-size: 0.7rem;
    font-family: var(--font-body);
    color: var(--gray-500);
    flex-shrink: 0;
  }

  .doc-object-doc-pct.partial {
    color: var(--force-cyan);
  }

  .doc-object-doc-pct.complete {
    color: var(--yoda-green);
  }

  .doc-suggestion-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4caf50;
    margin-left: auto;
    flex-shrink: 0;
  }

  .doc-custom-badge {
    background: var(--nebula-purple);
    color: #fff;
    font-size: 0.65rem;
    padding: 0.1rem 0.35rem;
    border-radius: 3px;
    font-weight: bold;
  }

  /* Detail pane */
  .doc-detail {
    flex: 1;
    min-width: 0;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid var(--gray-700);
    border-radius: 8px;
    overflow-y: auto;
    max-height: calc(100vh - 260px);
  }

  .doc-detail-empty,
  .doc-detail-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    color: var(--gray-500);
    gap: 1rem;
  }

  .doc-detail-content {
    padding: 1.5rem;
  }

  .doc-detail-header {
    display: flex;
    align-items: baseline;
    gap: 0.8rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
  }

  .doc-detail-header h3 {
    font-family: var(--font-display);
    color: var(--star-white);
    font-size: 1.3rem;
    margin: 0;
  }

  .doc-detail-api-name {
    color: var(--gray-400);
    font-family: var(--font-body);
    font-size: 0.85rem;
  }

  /* AI Description */
  .doc-ai-description {
    background: rgba(0, 102, 204, 0.08);
    border: 1px solid rgba(0, 102, 204, 0.2);
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .doc-ai-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--cosmic-blue);
    font-weight: bold;
  }

  .doc-ai-description p {
    color: var(--star-white);
    margin: 0.4rem 0 0;
    line-height: 1.5;
    font-size: 0.95rem;
  }

  /* Meta info */
  .doc-detail-meta {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
  }

  .doc-meta-item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .doc-meta-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--gray-500);
  }

  .doc-meta-value {
    font-size: 1.1rem;
    font-weight: bold;
    color: var(--star-white);
    font-family: var(--font-body);
  }

  /* Relationships */
  .doc-relationships {
    margin-bottom: 1.5rem;
  }

  .doc-relationships h4 {
    color: var(--gray-300);
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 0.5rem;
  }

  .doc-rel-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
  }

  .doc-rel-chip {
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--gray-700);
    color: var(--gray-300);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-family: var(--font-body);
    transition: all 0.15s;
  }

  .doc-rel-chip:hover {
    border-color: var(--force-cyan);
    color: var(--force-cyan);
  }

  .doc-rel-more {
    color: var(--gray-500);
    font-size: 0.75rem;
    padding: 0.2rem 0.5rem;
  }

  /* Fields */
  .doc-fields-section {
    margin-top: 1rem;
  }

  .doc-fields-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }

  .doc-fields-header h4 {
    color: var(--gray-300);
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0;
  }

  .doc-field-search {
    padding: 0.35rem 0.7rem;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--gray-700);
    border-radius: 6px;
    color: var(--star-white);
    font-family: var(--font-body);
    font-size: 0.8rem;
    outline: none;
    width: 200px;
  }

  .doc-field-search:focus {
    border-color: var(--force-cyan);
  }

  .doc-field-search::placeholder {
    color: var(--gray-600);
  }

  .doc-fields-table-wrapper {
    overflow-x: auto;
  }

  .doc-fields-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
  }

  .doc-fields-table th {
    text-align: left;
    padding: 0.5rem 0.7rem;
    border-bottom: 2px solid var(--gray-700);
    color: var(--gray-400);
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    position: sticky;
    top: 0;
    background: rgba(10, 10, 10, 0.95);
  }

  .doc-fields-table td {
    padding: 0.5rem 0.7rem;
    border-bottom: 1px solid var(--gray-800);
    color: var(--star-white);
    vertical-align: top;
  }

  .doc-fields-table tr:hover td {
    background: rgba(0, 212, 255, 0.03);
  }

  .doc-th-field {
    width: 20%;
  }

  .doc-th-desc {
    width: 80%;
  }

  .doc-field-info {
    width: 20%;
    vertical-align: top;
  }

  .doc-field-label {
    font-weight: bold;
    color: var(--star-white);
    margin-bottom: 0.25rem;
  }

  .doc-field-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
  }

  .doc-field-custom .doc-custom-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--nebula-purple);
    margin-right: 0.4rem;
  }

  .doc-field-api {
    font-family: var(--font-body);
    color: var(--gray-500);
    font-size: 0.75rem;
  }

  .doc-field-type {
    background: rgba(0, 0, 0, 0.4);
    padding: 0.1rem 0.35rem;
    border-radius: 3px;
    font-family: var(--font-body);
    font-size: 0.7rem;
    color: var(--droid-gold);
  }

  .doc-field-ref {
    color: var(--cosmic-blue);
    font-size: 0.7rem;
  }

  .doc-field-formula-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    background: rgba(0, 212, 255, 0.12);
    color: var(--force-cyan);
    border: 1px solid rgba(0, 212, 255, 0.25);
    padding: 0.05rem 0.4rem;
    border-radius: 3px;
    font-family: var(--font-body);
    font-size: 0.7rem;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s;
  }

  .doc-field-formula-badge:hover {
    background: rgba(0, 212, 255, 0.22);
  }

  .doc-field-formula-body {
    margin-top: 0.4rem;
    padding: 0.5rem 0.65rem;
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(0, 212, 255, 0.15);
    border-radius: 4px;
    font-family: var(--font-body);
    font-size: 0.72rem;
    color: var(--force-cyan);
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.5;
  }

  .doc-field-created-date {
    margin-left: auto;
    font-family: var(--font-body);
    font-size: 0.65rem;
    color: var(--gray-500);
    white-space: nowrap;
  }

  .doc-field-desc {
    width: 80%;
    color: var(--gray-300) !important;
    line-height: 1.4;
  }

  .doc-no-desc {
    color: var(--gray-600);
  }

  .doc-no-results {
    color: var(--gray-500);
    text-align: center;
    padding: 2rem 1rem;
  }

  .doc-error {
    color: var(--sith-red);
    margin-top: 1rem;
  }

  .doc-error-banner {
    position: fixed;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    background: var(--sith-red);
    color: #fff;
    padding: 0.8rem 1.5rem;
    border-radius: 8px;
    font-size: 0.9rem;
    z-index: 100;
  }

  /* AI Suggestion Banner */
  .doc-suggestion-banner {
    background: rgba(34, 139, 34, 0.08);
    border: 1px solid rgba(34, 139, 34, 0.3);
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .doc-suggestion-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }

  .doc-suggestion-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #4caf50;
    font-weight: bold;
  }

  .doc-suggestion-actions {
    display: flex;
    gap: 0.4rem;
  }

  .doc-suggestion-accept {
    background: rgba(34, 139, 34, 0.2);
    color: #4caf50;
    border: 1px solid rgba(34, 139, 34, 0.4);
    padding: 0.25rem 0.7rem;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .doc-suggestion-accept:hover:not(:disabled) {
    background: rgba(34, 139, 34, 0.35);
  }

  .doc-suggestion-dismiss {
    background: transparent;
    color: var(--gray-400);
    border: 1px solid var(--gray-700);
    padding: 0.25rem 0.7rem;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .doc-suggestion-dismiss:hover:not(:disabled) {
    border-color: var(--gray-500);
    color: var(--gray-300);
  }

  .doc-suggestion-text {
    color: var(--star-white);
    margin: 0;
    line-height: 1.5;
    font-size: 0.95rem;
    font-style: italic;
  }

  /* Field suggestion inline */
  .doc-field-suggestion {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    background: rgba(34, 139, 34, 0.06);
    border: 1px solid rgba(34, 139, 34, 0.2);
    border-radius: 4px;
    padding: 0.3rem 0.5rem;
    margin-bottom: 0.3rem;
    font-size: 0.8rem;
  }

  .doc-field-suggestion-text {
    flex: 1;
    color: var(--gray-300);
    font-style: italic;
    line-height: 1.4;
  }

  .doc-field-suggestion-actions {
    display: flex;
    gap: 0.2rem;
    flex-shrink: 0;
  }

  .doc-field-accept,
  .doc-field-dismiss {
    background: transparent;
    border: 1px solid var(--gray-700);
    color: var(--gray-400);
    width: 22px;
    height: 22px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.15s;
  }

  .doc-field-accept:hover:not(:disabled) {
    border-color: #4caf50;
    color: #4caf50;
    background: rgba(34, 139, 34, 0.15);
  }

  .doc-field-dismiss:hover:not(:disabled) {
    border-color: var(--sith-red);
    color: var(--sith-red);
    background: rgba(255, 0, 0, 0.08);
  }

  /* Accept All Button */
  .doc-accept-all-btn {
    background: rgba(34, 139, 34, 0.15);
    color: #4caf50;
    border: 1px solid rgba(34, 139, 34, 0.3);
    padding: 0.3rem 0.8rem;
    border-radius: 6px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .doc-accept-all-btn:hover:not(:disabled) {
    background: rgba(34, 139, 34, 0.25);
  }

  .doc-accept-all-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .doc-fields-header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  /* Light theme adjustments for suggestions */
  :root.light-side .doc-suggestion-banner {
    background: rgba(34, 139, 34, 0.05);
  }

  :root.light-side .doc-suggestion-text {
    color: var(--space-dark);
  }

  :root.light-side .doc-field-suggestion {
    background: rgba(34, 139, 34, 0.04);
  }

  :root.light-side .doc-field-suggestion-text {
    color: var(--gray-300);
  }

  /* Inline editing */
  .doc-editable {
    cursor: pointer;
    position: relative;
    display: inline-block;
    width: 100%;
  }

  .doc-editable .doc-edit-icon {
    opacity: 0;
    margin-left: 0.4rem;
    font-size: 0.8rem;
    color: var(--gray-500);
    transition: opacity 0.15s;
  }

  .doc-editable:hover .doc-edit-icon {
    opacity: 1;
  }

  .doc-edit-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.4rem;
  }

  .doc-edit-textarea {
    width: 100%;
    padding: 0.6rem;
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid var(--gray-700);
    border-radius: 6px;
    color: var(--star-white);
    font-family: var(--font-body);
    font-size: 0.9rem;
    line-height: 1.5;
    resize: vertical;
    outline: none;
  }

  .doc-edit-textarea:focus {
    border-color: var(--force-cyan);
  }

  .doc-edit-actions {
    display: flex;
    gap: 0.4rem;
  }

  .doc-edit-save {
    background: linear-gradient(135deg, var(--jedi-blue), var(--force-cyan));
    color: #fff;
    border: none;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.8rem;
    cursor: pointer;
  }

  .doc-edit-save:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .doc-edit-cancel {
    background: transparent;
    color: var(--gray-400);
    border: 1px solid var(--gray-700);
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.8rem;
    cursor: pointer;
  }

  .doc-edit-cancel:hover:not(:disabled) {
    border-color: var(--gray-500);
    color: var(--gray-300);
  }

  .doc-edit-field-group {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .doc-edit-field-textarea {
    width: 100%;
    padding: 0.5rem;
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid var(--force-cyan);
    border-radius: 4px;
    color: var(--star-white);
    font-family: var(--font-body);
    font-size: 0.85rem;
    line-height: 1.5;
    outline: none;
    resize: vertical;
    min-height: 60px;
  }

  .doc-edit-field-textarea:disabled {
    opacity: 0.6;
  }

  .doc-edit-field-actions {
    display: flex;
    gap: 0.3rem;
  }

  /* Light theme adjustments */
  :root.light-side .doc-search-input,
  :root.light-side .doc-field-search {
    background: rgba(255, 255, 255, 0.8);
    border-color: var(--gray-700);
  }

  :root.light-side .doc-object-list,
  :root.light-side .doc-detail {
    background: rgba(255, 255, 255, 0.6);
    border-color: var(--gray-700);
  }

  :root.light-side .doc-object-item {
    border-bottom-color: var(--gray-800);
  }

  :root.light-side .doc-fields-table th {
    background: rgba(255, 255, 255, 0.95);
  }

  :root.light-side .doc-ai-description {
    background: rgba(0, 102, 204, 0.05);
  }

  :root.light-side .doc-edit-textarea,
  :root.light-side .doc-edit-field-textarea {
    background: rgba(255, 255, 255, 0.9);
    color: var(--space-dark);
    border-color: var(--gray-700);
  }

  :root.light-side .doc-edit-textarea:focus,
  :root.light-side .doc-edit-field-textarea:focus {
    border-color: var(--force-cyan);
  }

  /* Generate button in header */
  .doc-generate-btn {
    background: linear-gradient(135deg, var(--jedi-blue), var(--force-cyan));
    color: #fff;
    border: none;
    padding: 0.4rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
  }

  .doc-generate-btn:hover {
    box-shadow: 0 2px 12px rgba(0, 212, 255, 0.3);
  }

  .doc-header-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
  }

  /* Checkbox in sidebar */
  .doc-object-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .doc-object-checkbox {
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    accent-color: var(--force-cyan);
    cursor: pointer;
  }

  .doc-object-item-content {
    flex: 1;
    min-width: 0;
  }

  .doc-object-item.checked {
    background: rgba(0, 212, 255, 0.08);
  }

  /* Org context section */
  .doc-org-context {
    background: rgba(0, 102, 204, 0.06);
    border: 1px solid rgba(0, 102, 204, 0.15);
    border-radius: 8px;
    padding: 0.8rem;
    margin-bottom: 1rem;
  }

  .doc-org-context-header {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    margin-bottom: 0.4rem;
  }

  .doc-org-context-hint {
    color: var(--gray-500);
    font-size: 0.75rem;
  }

  /* Selected objects list in detail pane */
  /* Review navigation */
  .doc-review-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--gray-700);
  }

  .doc-review-back {
    background: none;
    border: 1px solid var(--gray-600);
    color: var(--gray-300);
    padding: 0.4rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
  }

  .doc-review-back:hover {
    border-color: var(--force-cyan);
    color: var(--force-cyan);
  }

  .doc-review-stepper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .doc-review-prev,
  .doc-review-next {
    background: none;
    border: 1px solid var(--gray-600);
    color: var(--gray-300);
    padding: 0.4rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
  }

  .doc-review-prev:hover:not(:disabled),
  .doc-review-next:hover:not(:disabled) {
    border-color: var(--force-cyan);
    color: var(--force-cyan);
  }

  .doc-review-prev:disabled,
  .doc-review-next:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  .doc-review-position {
    color: var(--gray-400);
    font-size: 0.8rem;
    min-width: 60px;
    text-align: center;
  }

  .doc-selected-heading {
    color: var(--gray-300);
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 0.75rem;
  }

  .doc-selected-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .doc-selected-item {
    border: 1px solid var(--gray-700);
    border-radius: 8px;
    padding: 0.75rem;
    background: rgba(0, 0, 0, 0.15);
  }

  .doc-selected-item-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }

  .doc-selected-item-name {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: var(--star-white);
  }

  .doc-selected-item-name strong {
    color: var(--star-white);
  }

  .doc-selected-api-name {
    color: var(--gray-500);
    font-family: var(--font-body);
    font-size: 0.75rem;
    opacity: 0.7;
  }

  .doc-auto-resize {
    resize: none;
    overflow: hidden;
  }

  .doc-selected-item-actions {
    display: flex;
    gap: 0.3rem;
    align-items: center;
  }

  .doc-selected-view {
    background: transparent;
    border: 1px solid var(--gray-700);
    color: var(--gray-400);
    padding: 0.15rem 0.5rem;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.7rem;
    transition: all 0.15s;
  }

  .doc-selected-view:hover {
    border-color: var(--force-cyan);
    color: var(--force-cyan);
  }

  .doc-selected-remove {
    background: transparent;
    border: 1px solid var(--gray-700);
    color: var(--gray-500);
    width: 22px;
    height: 22px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.15s;
  }

  .doc-selected-remove:hover {
    border-color: var(--sith-red);
    color: var(--sith-red);
  }

  .doc-selected-suggestion {
    background: rgba(34, 139, 34, 0.08);
    border: 1px solid rgba(34, 139, 34, 0.25);
    border-radius: 6px;
    padding: 0.6rem;
    margin-bottom: 0.6rem;
  }

  .doc-selected-suggestion-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.3rem;
  }

  .doc-desc-label {
    display: block;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--gray-500);
    margin-bottom: 0.25rem;
  }

  .doc-selected-desc-textarea {
    width: 100%;
    padding: 0.5rem;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--gray-700);
    border-radius: 6px;
    color: var(--star-white);
    font-family: var(--font-body);
    font-size: 0.85rem;
    line-height: 1.5;
    resize: none;
    outline: none;
    overflow: hidden;
    min-height: 36px;
  }

  .doc-selected-desc-textarea:focus {
    border-color: var(--force-cyan);
  }

  .doc-selected-desc-textarea::placeholder {
    color: var(--gray-600);
  }

  /* Light theme adjustments for new elements */
  :root.light-side .doc-org-context {
    background: rgba(0, 102, 204, 0.04);
  }

  :root.light-side .doc-selected-item {
    background: rgba(255, 255, 255, 0.4);
  }

  :root.light-side .doc-selected-desc-textarea {
    background: rgba(255, 255, 255, 0.8);
    color: var(--space-dark);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .doc-layout {
      flex-direction: column;
    }

    .doc-sidebar {
      width: 100%;
    }

    .doc-object-list {
      max-height: 200px;
    }

    .doc-detail {
      max-height: none;
    }

    .doc-detail-meta {
      gap: 1rem;
    }

    .doc-fields-header {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
    }

    .doc-field-search {
      width: 100%;
    }

    .doc-header-actions {
      flex-wrap: wrap;
    }
  }

  /* Deploy button */
  .doc-deploy-btn {
    background: linear-gradient(135deg, #2e7d32, #43a047);
    color: #fff;
    border: none;
    padding: 0.4rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .doc-deploy-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #388e3c, #4caf50);
    box-shadow: 0 2px 12px rgba(76, 175, 80, 0.3);
  }

  .doc-deploy-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .doc-deploy-disabled {
    color: var(--gray-500);
    font-size: 0.75rem;
    font-style: italic;
    cursor: default;
  }

  .doc-deploy-timestamp {
    color: var(--gray-400);
    font-size: 0.75rem;
    white-space: nowrap;
  }

  /* Deploy result banner */
  .doc-deploy-result-banner {
    position: fixed;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(46, 125, 50, 0.95);
    color: #fff;
    padding: 0.7rem 1.2rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 0.9rem;
    z-index: 1000;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  }

  .doc-deploy-result-close {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    font-size: 1rem;
    padding: 0;
    line-height: 1;
  }

  .doc-deploy-result-close:hover {
    color: #fff;
  }

  .doc-deploy-result-banner.has-errors {
    background: rgba(198, 120, 20, 0.95);
  }

  .doc-deploy-result-content {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .doc-deploy-error-list {
    font-size: 0.8rem;
    opacity: 0.9;
    max-height: 120px;
    overflow-y: auto;
  }

  .doc-deploy-error-item {
    padding: 0.15rem 0;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }

  /* Sidebar selected compact list */
  .doc-sidebar-selected {
    border: 1px solid rgba(0, 212, 255, 0.25);
    border-radius: 8px;
    background: rgba(0, 212, 255, 0.05);
    padding: 0.5rem;
    max-height: 180px;
    display: flex;
    flex-direction: column;
  }

  .doc-sidebar-selected-heading {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--force-cyan);
    font-weight: bold;
    margin-bottom: 0.35rem;
    padding: 0 0.15rem;
  }

  .doc-sidebar-selected-list {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .doc-sidebar-selected-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.25rem 0.4rem;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s;
  }

  .doc-sidebar-selected-item:hover {
    background: rgba(0, 212, 255, 0.1);
  }

  .doc-sidebar-selected-label {
    color: var(--star-white);
    font-size: 0.8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .doc-sidebar-selected-remove {
    background: transparent;
    border: none;
    color: var(--gray-500);
    cursor: pointer;
    font-size: 0.65rem;
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    flex-shrink: 0;
    transition: all 0.15s;
    line-height: 1;
  }

  .doc-sidebar-selected-remove:hover {
    color: var(--sith-red);
    background: rgba(255, 0, 0, 0.1);
  }

  /* Ignore button on object rows */
  .doc-ignore-btn {
    background: transparent;
    border: none;
    color: var(--gray-600);
    cursor: pointer;
    font-size: 0.9rem;
    padding: 0.1rem 0.25rem;
    border-radius: 3px;
    flex-shrink: 0;
    opacity: 0;
    transition: all 0.15s;
    line-height: 1;
  }

  .doc-object-item:hover .doc-ignore-btn {
    opacity: 1;
  }

  .doc-ignore-btn:hover {
    color: var(--sith-red);
    background: rgba(255, 0, 0, 0.1);
  }

  /* Ignored list items */
  .doc-ignored-item {
    cursor: default;
  }

  .doc-restore-btn {
    background: transparent;
    color: var(--force-cyan);
    border: 1px solid rgba(0, 212, 255, 0.3);
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    font-size: 0.7rem;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s;
  }

  .doc-restore-btn:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: var(--force-cyan);
  }

  :root.light-side .doc-sidebar-selected {
    background: rgba(0, 102, 204, 0.04);
    border-color: rgba(0, 102, 204, 0.2);
  }

  /* Tab navigation */
  .doc-tabs {
    display: flex;
    gap: 0.25rem;
    margin-left: 1rem;
  }

  .doc-tab {
    background: transparent;
    border: 1px solid var(--gray-700);
    color: var(--gray-400);
    padding: 0.35rem 0.8rem;
    border-radius: 6px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-body);
  }

  .doc-tab:hover {
    border-color: var(--force-cyan);
    color: var(--force-cyan);
  }

  .doc-tab.active {
    background: var(--force-cyan);
    border-color: var(--force-cyan);
    color: #000;
    font-weight: 600;
  }

  /* Flow type badges */
  .doc-flow-type-badge {
    display: inline-block;
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    padding: 0.1rem 0.35rem;
    border-radius: 3px;
    margin-right: 0.4rem;
    letter-spacing: 0.03em;
    vertical-align: middle;
    background: var(--gray-700);
    color: var(--gray-300);
  }

  .doc-flow-type-badge.doc-flow-type-autolaunchedflow {
    background: rgba(128, 128, 128, 0.2);
    color: var(--gray-400);
  }

  .doc-flow-type-badge.doc-flow-type-flow {
    background: rgba(34, 197, 94, 0.15);
    color: #22c55e;
  }

  .doc-flow-trigger-chip {
    display: inline-block;
    font-size: 0.65rem;
    background: rgba(0, 212, 255, 0.1);
    color: var(--force-cyan);
    padding: 0.05rem 0.4rem;
    border-radius: 3px;
    margin-left: 0.4rem;
  }

  /* Flow graph container */
  .doc-flow-graph-container {
    height: 600px;
    border: 1px solid var(--gray-700);
    border-radius: 8px;
    overflow: hidden;
    margin-top: 1rem;
  }

  /* Flow element grid */
  .doc-flow-elements h4 {
    color: var(--gray-300);
    font-size: 0.85rem;
    margin: 1rem 0 0.5rem;
  }

  .doc-flow-element-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .doc-flow-element-item {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    background: var(--gray-800);
    padding: 0.3rem 0.6rem;
    border-radius: 4px;
    font-size: 0.78rem;
  }

  .doc-flow-element-type {
    color: var(--gray-400);
    text-transform: capitalize;
  }

  .doc-flow-element-count {
    font-weight: 700;
    color: var(--force-cyan);
  }

  /* Flow field refs */
  .doc-flow-field-refs h4 {
    color: var(--gray-300);
    font-size: 0.85rem;
    margin: 1rem 0 0.5rem;
  }

  .doc-flow-ref-group {
    margin-bottom: 0.6rem;
  }

  .doc-flow-ref-object {
    font-weight: 600;
    color: var(--force-cyan);
    font-size: 0.82rem;
    margin-bottom: 0.25rem;
  }

  .doc-flow-ref-object:hover {
    text-decoration: underline;
  }

  .doc-flow-ref-fields {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
  }

  .doc-flow-ref-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.72rem;
    padding: 0.15rem 0.45rem;
    border-radius: 3px;
    background: var(--gray-800);
    color: var(--gray-300);
  }

  .doc-flow-ref-op {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .doc-flow-op-read .doc-flow-ref-op { color: #3b82f6; }
  .doc-flow-op-create .doc-flow-ref-op { color: #22c55e; }
  .doc-flow-op-update .doc-flow-ref-op { color: #f59e0b; }
  .doc-flow-op-delete .doc-flow-ref-op { color: #ef4444; }

  /* Flow connections */
  .doc-flow-connections h4 {
    color: var(--gray-300);
    font-size: 0.85rem;
    margin: 1rem 0 0.5rem;
  }

  .doc-flow-conn-section {
    margin-bottom: 0.5rem;
  }

  .doc-flow-conn-label {
    font-size: 0.75rem;
    color: var(--gray-500);
    margin-right: 0.5rem;
  }

  .doc-rel-inferred {
    border-style: dashed !important;
    opacity: 0.8;
  }

  .doc-flow-conn-inferred-label {
    font-size: 0.55rem;
    font-style: italic;
    color: var(--gray-500);
    margin-left: 0.3rem;
  }

  .doc-flow-object-link:hover {
    color: var(--force-cyan) !important;
    text-decoration: underline;
  }

  .doc-flow-ops-mini {
    font-size: 0.55rem;
    font-weight: 700;
    color: var(--force-cyan);
    margin-left: 0.3rem;
    opacity: 0.7;
  }

  /* Light mode overrides for flow elements */
  :root.light-side .doc-tab {
    border-color: #d1d5db;
    color: #6b7280;
  }

  :root.light-side .doc-tab:hover {
    border-color: #0066cc;
    color: #0066cc;
  }

  :root.light-side .doc-tab.active {
    background: #0066cc;
    border-color: #0066cc;
    color: #fff;
  }

  :root.light-side .doc-flow-type-badge {
    background: #e5e7eb;
    color: #6b7280;
  }

  :root.light-side .doc-flow-trigger-chip {
    background: rgba(0, 102, 204, 0.08);
    color: #0066cc;
  }

  :root.light-side .doc-flow-element-item {
    background: #f3f4f6;
  }

  :root.light-side .doc-flow-ref-chip {
    background: #f3f4f6;
    color: #374151;
  }

  :root.light-side .doc-flow-graph-container {
    border-color: #d1d5db;
  }
`;

export default DocumentationBrowser;
